import { useStores } from "../../../models/helpers/useStores"
import { useEffect, useMemo } from "react"
import branchio from "branch-sdk"
import { ITrip } from "@cruisen/contract"
import { updateTrip } from "../../../services/firestore/trips"
import { makeAutoObservable } from "mobx"
import Config from "../../../config"
import { Analytics } from "../../../utils/analytics"

class ShareLinkStore {
  isLinkCopied: boolean
  shareLink: string | null

  constructor() {
    this.isLinkCopied = false
    this.shareLink = null

    makeAutoObservable(this)
  }

  setIsLinkCopied(isLinkCopied: boolean) {
    this.isLinkCopied = isLinkCopied
  }

  setShareLink(shareLink: string | null) {
    this.shareLink = shareLink
  }
}

const publicBranchioKey = Config.branchKeyLive

export const useShareLink = () => {
  const rootStore = useStores()
  const store = useMemo(() => new ShareLinkStore(), [])

  const copyLinkToClipboard = (link: string) => {
    navigator.clipboard.writeText(link)
    store.setIsLinkCopied(true)
    console.log("Link is copied to clipboard", link)

    setTimeout(() => {
      store.setIsLinkCopied(false)
    }, 3000)
  }

  const saveLinkToFirestore = async (inviteLink: string) => {
    const tripPatch: Partial<ITrip> = {
      collaborators: rootStore.activeTrip.collaborators.map((collaboratorItem) => {
        if (collaboratorItem.userId === rootStore.loggedInUser.id) {
          return {
            ...collaboratorItem,
            inviteLink,
          }
        }
        return collaboratorItem
      }),
    }

    if (!rootStore.activeTrip.inviteLink) {
      tripPatch.inviteLink = inviteLink
    }

    await updateTrip(rootStore.activeTrip.id, tripPatch)
  }

  const copyLink = () => {
    if (!store.shareLink) {
      alert(
        "Link generation may not work properly if browser blockers are enabled; please disable them for the best experience.",
      )
      return
    }
    Analytics.logShare(rootStore.activeTrip!.id)
    copyLinkToClipboard(store.shareLink)
  }

  const generateShareLink = () => {
    if (!rootStore.activeTrip) return

    if (rootStore.activeTrip.inviteLink) {
      store.setShareLink(rootStore.activeTrip.inviteLink)
      return
    }

    const collaborator = rootStore.activeTrip.collaborators.find(
      (collaborator) => collaborator.userId === rootStore.loggedInUser.id,
    )

    if (collaborator?.inviteLink) {
      copyLinkToClipboard(collaborator.inviteLink)
      return
    }

    const branchData = {
      tripId: rootStore.activeTrip!.id,
      tripName: rootStore.activeTrip!.name,
      invitingUser: `${rootStore.loggedInUser.firstName} ${rootStore.loggedInUser.lastName}`,
      invitingUserId: rootStore.loggedInUser.id,
      referralCode: "",
      agentCode: rootStore.loggedInUser.agentCode,
    }

    branchio.link(
      {
        data: branchData,
      },
      async (err, link) => {
        if (err) {
          console.log("Error creating link", err)
          return
        }

        try {
          store.setShareLink(link)
          await saveLinkToFirestore(link)
        } catch (err) {
          console.log("Error saving link to firestore", err)
        }
      },
    )
  }

  useEffect(() => {
    branchio.init(publicBranchioKey, {}, () => {
      generateShareLink()
    })
  }, [rootStore.activeTrip?.id])

  return {
    copyLink,
    shareLink: store.shareLink,
    isLinkCopied: store.isLinkCopied,
  }
}
